import { useCallback, useMemo, useState } from 'react'

import {
  CustomFieldType,
  ShortContentFormat,
} from '@tribeplatform/gql-client/types'
import { NonDraggableItem } from '@tribeplatform/react-components/common/components'
import { CustomFieldViews } from '@tribeplatform/react-components/CustomField'
import { supportedFieldViewsMapping } from '@tribeplatform/react-components/CustomFields'
import { T, useI18n, useStaticIntl } from '@tribeplatform/react-components/i18n'
import { getDefaultActionBar } from '@tribeplatform/react-components/Post'
import {
  SortOptionId,
  sortOptionsFactory,
} from '@tribeplatform/react-components/Reply'
import {
  FieldsSortableList,
  GenericFieldSettings,
  useFields,
  StaticFieldTypes,
  ReactionCountPositions,
} from '@tribeplatform/react-components/Views'
import type {
  SetActionBarSettings,
  SetFieldProperty,
  StaticField,
  ViewField,
} from '@tribeplatform/react-components/Views'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { Toggle } from '@tribeplatform/react-ui-kit/Toggle'

import { useBannerSources } from '../hooks/useBannerSources.js'
import type { PostsBlockView } from '../types.js'
import {
  avatarSizesFactory,
  bannerAspectRatiosFactory,
  bannerStylesFactory,
  actionVariantOptionsFactory,
  displayStylesFactory,
  titleSizesFactory,
  truncateSizesFactory,
  contentFormatsFactory,
  reactionCountPositionsOptionsFactory,
} from '../utils/selectItems.js'

import { ActionBarSettings } from './ActionBarSettings.js'

interface Props {
  field: StaticField
  viewStyle: PostsBlockView['style']
  postTypeSlugs?: PostsBlockView['postTypeSlugs']
  setProperty: SetFieldProperty
  goBack: () => void
  isSubField?: boolean
  isSingleBlock?: boolean
}
export const FieldSettings = ({
  field,
  viewStyle,
  postTypeSlugs,
  setProperty,
  goBack,
  isSubField = false,
  isSingleBlock,
}: Props) => {
  const { $t } = useI18n()
  const avatarSizes = useStaticIntl(avatarSizesFactory)
  const bannerAspectRatios = useStaticIntl(bannerAspectRatiosFactory)
  const bannerStyles = useStaticIntl(bannerStylesFactory)
  const actionVariantOptions = useStaticIntl(actionVariantOptionsFactory)
  const reactionCountPositionsOptions = useStaticIntl(
    reactionCountPositionsOptionsFactory,
  )
  const displayStyles = useStaticIntl(displayStylesFactory)
  const titleSizes = useStaticIntl(titleSizesFactory)
  const truncateSizes = useStaticIntl(truncateSizesFactory)
  const contentFormats = useStaticIntl(contentFormatsFactory)
  const replySortOptions = useStaticIntl(sortOptionsFactory)
  const [buttonNameError, setButtonNameError] = useState(false)
  const {
    setFieldProperty,
    showFieldSettings,
    hideFieldSettings,
    setActiveFieldId,
    getFieldSettings,
    setFieldSettings,
    selectedFieldId,
  } = useFields({
    field,
    fieldItems: field?.fields,
    setFields: newFields => setProperty('fields', newFields),
    setSettings: newSettings => setProperty('settings', newSettings),
  })
  const bannerSources = useBannerSources(postTypeSlugs)

  const getCustomFieldSettings = (key: string) => {
    const settings = getFieldSettings('customFieldSettings')?.find(
      settings => settings.key === key,
    )?.value
    if (settings) {
      try {
        return JSON.parse(settings)
      } catch (err) {
        logger.error('Cannot parse value in custom field')
        return null
      }
    }

    return undefined
  }

  const setCustomFieldSettings = (
    key: string,
    value: string | Record<string, string>,
  ) => {
    const prevSettings =
      getFieldSettings('customFieldSettings')?.filter(
        settings => settings.key !== key,
      ) ?? []
    setFieldSettings('customFieldSettings', [
      ...prevSettings,
      { key, value: JSON.stringify(value) },
    ])
  }

  const getActionBarSettings = useCallback(() => {
    return getFieldSettings('actionBar') ?? getDefaultActionBar()
  }, [getFieldSettings])

  const replySortId = useMemo(() => {
    const replySortId = getFieldSettings('defaultReplySortId')

    if (isSingleBlock) {
      return replySortId || SortOptionId.latest
    }

    return replySortId || SortOptionId.oldest
  }, [getFieldSettings, isSingleBlock])

  const setActionBarSettings = useCallback<SetActionBarSettings>(
    (key, value) => {
      const prevSettings = getActionBarSettings()
      const newSettings = { ...prevSettings, [key]: value }

      setFieldSettings('actionBar', newSettings)
    },
    [getActionBarSettings, setFieldSettings],
  )

  const supportedFieldViews =
    supportedFieldViewsMapping[getCustomFieldSettings('subtype')] ||
    supportedFieldViewsMapping[field.type]

  const toggleItem = (id: string) => {
    const newItems = field.fields.map(item =>
      item.id === id ? { ...item, enabled: !item.enabled } : item,
    )
    setProperty('fields', newItems)
  }

  if (field.id === 'actionBar') {
    return (
      <ActionBarSettings
        actionBar={getActionBarSettings()}
        fullWidth={getFieldSettings('fullWidth')}
        setFullWidth={fullWidth => setFieldSettings('fullWidth', fullWidth)}
        goBack={goBack}
        enabled={field.enabled}
        setEnabled={enabled => setProperty('enabled', enabled)}
        setSettings={setActionBarSettings}
        hasMultiplePostTypes={postTypeSlugs?.length > 1}
      />
    )
  }

  return (
    <>
      <GenericFieldSettings
        goBack={goBack}
        title={field.label}
        enabled={field.enabled}
        setEnabled={enabled => setProperty('enabled', enabled)}
      >
        {!isSubField &&
          viewStyle !== 'gallery' &&
          viewStyle !== 'carousel' &&
          viewStyle !== 'card' &&
          field.type !== StaticFieldTypes.avatar &&
          field.type !== StaticFieldTypes.banner && (
            <Toggle
              title={$t({
                defaultMessage: 'Stretch',
                id: 'FieldSettings.ShouldStretch',
              })}
              checked={getFieldSettings('stretchX')}
              onChange={value => {
                setFieldSettings('stretchX', value)
              }}
            />
          )}
        {!isSubField &&
          (viewStyle === 'gallery' ||
            viewStyle === 'carousel' ||
            viewStyle === 'card') &&
          field.type !== StaticFieldTypes.avatar &&
          field.type !== StaticFieldTypes.banner &&
          field.id !== 'galleryMemberInfo' &&
          field.id !== 'replyList' &&
          field.id !== 'pinnedReply' &&
          field.id !== 'postInfoBar' && (
            <Toggle
              title={$t({
                defaultMessage: 'Full width',
                id: 'FieldSettings.IsFullWidth',
              })}
              checked={getFieldSettings('fullWidth')}
              onChange={value => {
                setFieldSettings('fullWidth', value)
              }}
            />
          )}
        {!isSubField &&
          (viewStyle === 'gallery' || viewStyle === 'carousel') &&
          field.type !== StaticFieldTypes.avatar &&
          field.type !== StaticFieldTypes.banner &&
          field.id !== 'galleryMemberInfo' &&
          field.id !== 'replyList' &&
          field.id !== 'pinnedReply' &&
          field.id !== 'postInfoBar' && (
            <Toggle
              title={$t({
                defaultMessage: 'Stretch',
                id: 'FieldSettings.ShouldStretch',
              })}
              checked={getFieldSettings('stretchY')}
              onChange={value => {
                setFieldSettings('stretchY', value)
              }}
            />
          )}
        {!isSubField &&
          field.id === 'galleryMemberInfo' &&
          (viewStyle === 'gallery' || viewStyle === 'carousel') && (
            <Toggle
              title={$t({
                defaultMessage: 'Snap to bottom',
                id: 'PostsBlock.Settings.Fields.SnapBottom',
              })}
              checked={getFieldSettings('snapBottom')}
              onChange={value => {
                setFieldSettings('snapBottom', value)
              }}
            />
          )}
        {!isSubField && field.id === 'galleryMemberInfo' && (
          <Toggle
            title={$t({
              defaultMessage: 'Show {SPACE} name',
              id: 'PostsBlock.Settings.Fields.ShowSpace',
            })}
            checked={getFieldSettings('showSpaceName')}
            onChange={value => {
              setFieldSettings('showSpaceName', value)
            }}
          />
        )}
        {!isSubField && field.id === 'cardMemberInfo' && (
          <Toggle
            title="Show space name"
            checked={getFieldSettings('showSpaceName')}
            onChange={value => {
              setFieldSettings('showSpaceName', value)
            }}
          />
        )}
        {!isSubField &&
          field.id === 'cardMemberInfo' &&
          viewStyle === 'card' && (
            <Toggle
              title={$t({
                defaultMessage: 'Show post options',
                id: 'PostsBlock.Settings.Fields.PostOpts',
              })}
              checked={getFieldSettings('showPostOptions')}
              onChange={value => {
                setFieldSettings('showPostOptions', value)
              }}
            />
          )}
        {!isSubField && field.id === 'cardPostInfoBar' && (
          <>
            <Toggle
              title={$t({
                defaultMessage: 'Show reaction count',
                id: 'PostsBlock.Settings.Fields.ReactionsCount',
              })}
              checked={getFieldSettings('showReactionCount')}
              onChange={value => {
                setFieldSettings('showReactionCount', value)
              }}
            />
            <Toggle
              title={$t({
                defaultMessage: 'Show follow count',
                id: 'PostsBlock.Settings.Fields.FollowCount',
              })}
              checked={getFieldSettings('showFollowCount')}
              onChange={value => {
                setFieldSettings('showFollowCount', value)
              }}
            />
            <Toggle
              title={$t({
                defaultMessage: 'Show reply count',
                id: 'PostsBlock.Settings.Fields.ReplyCount',
              })}
              checked={getFieldSettings('showReplyCount')}
              onChange={value => {
                setFieldSettings('showReplyCount', value)
              }}
            />
          </>
        )}
        {!isSubField && field.id === 'postInfoBar' && isSingleBlock && (
          <Toggle
            title="Show back button"
            checked={getFieldSettings('showBackToSpaceButton')}
            onChange={value => {
              setFieldSettings('showBackToSpaceButton', value)
            }}
          />
        )}
        {viewStyle !== 'list' && !field.isStatic && (
          <Toggle
            title={$t({
              defaultMessage: 'Show field name',
              id: 'FieldSettings.ShowFieldName',
            })}
            checked={getFieldSettings('showFieldName')}
            onChange={value => {
              setFieldSettings('showFieldName', value)
            }}
          />
        )}
        {field.type === StaticFieldTypes.button && (
          <FormControl.Select
            name="buttonVariant"
            label={$t({ defaultMessage: 'Variant', id: 'Generics.Variant' })}
            value={getFieldSettings('buttonVariant')}
            onChange={value => {
              setFieldSettings(
                'buttonVariant',
                value as ViewField['settings']['buttonVariant'],
              )
            }}
            items={actionVariantOptions}
          />
        )}
        {field.id === 'reactButton' && (
          <>
            <Toggle
              title={$t({
                defaultMessage: 'Show reaction count',
                id: 'PostsBlock.Settings.Fields.ReactionsCount',
              })}
              checked={getFieldSettings('showReactionCount') ?? true}
              onChange={value => {
                setFieldSettings('showReactionCount', value)
              }}
            />
            {!!(getFieldSettings('showReactionCount') ?? true) && (
              <FormControl.Select
                name="buttonVariant"
                label={$t({
                  defaultMessage: 'Reaction count position',
                  id: 'PostsBlock.Settings.Fields.ReactionCountPosition',
                })}
                value={
                  getFieldSettings('reactionCountPosition') ??
                  ReactionCountPositions.below
                }
                onChange={value => {
                  setFieldSettings(
                    'reactionCountPosition',
                    value as ViewField['settings']['reactionCountPosition'],
                  )
                }}
                items={reactionCountPositionsOptions}
              />
            )}
          </>
        )}
        {supportedFieldViews?.length > 0 && (
          <FormControl.Select
            name="displayAppearance"
            label={$t({
              defaultMessage: 'Display appearance',
              id: 'PostsBlock.Settings.Fields.ViewAppearance',
            })}
            value={getCustomFieldSettings('view')}
            onChange={value => setCustomFieldSettings('view', value as string)}
            items={supportedFieldViews.map(fieldView => ({
              value: fieldView,
              text: fieldView,
            }))}
          />
        )}
        {getCustomFieldSettings('view') === CustomFieldViews.Button && (
          <FormControl.Input
            name="buttonText"
            label={$t({
              defaultMessage: 'Button text',
              id: 'Generics.ButtonText',
            })}
            value={
              buttonNameError
                ? ''
                : (getCustomFieldSettings('viewProps')?.text ?? '')
            }
            error={$t({
              defaultMessage: 'Button text cannot be empty',
              id: 'PostsBlock.Settings.Fields.ButtonText.Validation',
            })}
            invalid={buttonNameError}
            onChange={e => {
              const { value } = e.target

              if (!value) {
                setButtonNameError(true)
              } else {
                const viewProps = getCustomFieldSettings('viewProps')
                setButtonNameError(false)
                setCustomFieldSettings('viewProps', {
                  ...viewProps,
                  text: value,
                })
              }
            }}
          />
        )}
        {field.type === CustomFieldType.date && (
          <FormControl.Select
            name="dateFormat"
            label={$t({
              defaultMessage: 'Date format',
              id: 'Generics.DateFormat',
            })}
            value={getCustomFieldSettings('viewProps')?.format}
            onChange={value => {
              const currentSettings = getCustomFieldSettings('viewProps')

              if (currentSettings?.format) {
                delete currentSettings.format
              }

              if (value) {
                setCustomFieldSettings('viewProps', {
                  ...currentSettings,
                  format: value as string,
                })
              } else {
                setCustomFieldSettings('viewProps', currentSettings)
              }
            }}
            items={[
              {
                text: $t({
                  defaultMessage: 'Locale date',
                  id: 'Generics.LocaleDate',
                }),
                value: undefined,
              },
              {
                text: $t({
                  defaultMessage: 'Standard',
                  id: 'Generics.Standard',
                }),
                value: 'standard',
              },
              {
                text: $t({
                  defaultMessage: 'Relative date',
                  id: 'Generics.RelativeDate',
                }),
                value: 'relative',
              },
            ]}
          />
        )}
        {field.type === StaticFieldTypes.avatar && (
          <FormControl.Select
            name="avatarSize"
            label={$t({
              defaultMessage: 'Avatar size',
              id: 'FieldSettings.AvatarSize',
            })}
            value={getFieldSettings('avatarSize')}
            onChange={value => {
              setFieldSettings(
                'avatarSize',
                value as ViewField['settings']['avatarSize'],
              )
            }}
            items={avatarSizes}
          />
        )}
        {(field.type === StaticFieldTypes.title ||
          getCustomFieldSettings('view') === 'Title') && (
          <FormControl.Select
            name="titleSizes"
            label={$t({
              defaultMessage: 'Title size',
              id: 'FieldSettings.TitleSize',
            })}
            value={getFieldSettings('titleSize') || titleSizes[2].value}
            onChange={value => {
              setFieldSettings(
                'titleSize',
                value as ViewField['settings']['titleSize'],
              )
            }}
            items={titleSizes}
          />
        )}
        {field.type === StaticFieldTypes.banner && (
          <FormControl.Select
            name="bannerSource"
            label={$t({
              defaultMessage: 'Banner source',
              id: 'PostsBlock.Settings.Fields.BannerSource',
            })}
            value={getFieldSettings('bannerSource')}
            onChange={value => {
              setFieldSettings(
                'bannerSource',
                value as ViewField['settings']['bannerSource'],
              )
            }}
            items={bannerSources}
          />
        )}
        {field.type === StaticFieldTypes.banner &&
          field.id !== 'cardBanner' && (
            <FormControl.Select
              name="bannerStyle"
              label={$t({
                defaultMessage: 'Banner style',
                id: 'FieldSettings.BannerStyle',
              })}
              value={getFieldSettings('bannerStyle')}
              onChange={value => {
                setFieldSettings(
                  'bannerStyle',
                  value as ViewField['settings']['bannerStyle'],
                )
              }}
              items={bannerStyles}
            />
          )}
        {field.type === StaticFieldTypes.banner &&
          field.settings?.bannerStyle !== 'square' && (
            <FormControl.Select
              name="aspectRatio"
              label={$t({
                defaultMessage: 'Aspect ratio',
                id: 'FieldSettings.BannerAspectRatio',
              })}
              value={
                getFieldSettings('aspectRatio') || bannerAspectRatios[3].value
              }
              onChange={value => {
                setFieldSettings(
                  'aspectRatio',
                  value as ViewField['settings']['aspectRatio'],
                )
              }}
              items={bannerAspectRatios}
            />
          )}
        {field.id === 'cardPostContent' && (
          <FormControl.Select
            name="displayStyle"
            label={$t({
              defaultMessage: 'Display style',
              id: 'PostsBlock.Settings.Fields.DisplayStyle',
            })}
            value={getFieldSettings('displayStyle') || displayStyles[2].value}
            onChange={value => {
              setFieldSettings(
                'displayStyle',
                value as ViewField['settings']['displayStyle'],
              )
            }}
            items={displayStyles}
          />
        )}
        {field.id === 'cardPostContent' &&
          (field.settings?.displayStyle === 'truncated' ||
            field.settings?.displayStyle === 'expandable' ||
            field.settings?.displayStyle === undefined) && (
            <FormControl.Select
              name="truncateSize"
              label={$t({
                defaultMessage: 'Content preview size',
                id: 'PostsBlock.Settings.Fields.ContentPreviewSize',
              })}
              value={getFieldSettings('truncateSize') || truncateSizes[1].value}
              onChange={value => {
                setFieldSettings(
                  'truncateSize',
                  value as ViewField['settings']['truncateSize'],
                )
              }}
              items={truncateSizes}
            />
          )}
        {field.type === CustomFieldType.richText && (
          <FormControl.Select
            name="displayStyle"
            label={$t({
              defaultMessage: 'Display style',
              id: 'PostsBlock.Settings.Fields.DisplayStyle',
            })}
            value={getFieldSettings('displayStyle') || displayStyles[0].value}
            onChange={value => {
              setFieldSettings(
                'displayStyle',
                value as ViewField['settings']['displayStyle'],
              )
            }}
            items={displayStyles}
          />
        )}
        {field.type === CustomFieldType.richText &&
          (field.settings?.displayStyle === 'truncated' ||
            field.settings?.displayStyle === 'expandable') && (
            <FormControl.Select
              name="truncateSize"
              label={$t({
                defaultMessage: 'Content preview size',
                id: 'PostsBlock.Settings.Fields.ContentPreviewSize',
              })}
              value={getFieldSettings('truncateSize') || truncateSizes[1].value}
              onChange={value => {
                setFieldSettings(
                  'truncateSize',
                  value as ViewField['settings']['truncateSize'],
                )
              }}
              items={truncateSizes}
            />
          )}
        {(field.id === 'galleryPostContent' ||
          field.id === 'listPostContent') && (
          <>
            <FormControl.Select
              name="contentFormat"
              label={$t({
                defaultMessage: 'Content format',
                id: 'PostsBlock.Settings.Fields.ContentFormat',
              })}
              value={
                getFieldSettings('contentFormat') || contentFormats[1].value
              }
              onChange={value => {
                setFieldSettings(
                  'contentFormat',
                  value as ViewField['settings']['contentFormat'],
                )
              }}
              items={contentFormats}
              helperText={
                field.settings?.contentFormat === ShortContentFormat.Text ? (
                  <T
                    defaultMessage="This option will exclude the formatting, media, and embeds from original content."
                    id="PostsBlock.Settings.Fields.PlainTextHelper"
                  />
                ) : (
                  <T
                    defaultMessage="This option supports various types of formatting, media, and embeds."
                    id="PostsBlock.Settings.Fields.RichTextHelper"
                  />
                )
              }
            />
            {field.settings?.contentFormat !== ShortContentFormat.Text && (
              <Alert status="warning">
                <T
                  defaultMessage="Please be advised that allowing rich text for this layout might cause some issues on the user interface."
                  id="PostsBlock.Settings.Fields.AllowRichText"
                />
              </Alert>
            )}
          </>
        )}
        {field.id === 'customFields' && (
          <>
            {viewStyle !== 'list' && (
              <Toggle
                title={$t({
                  defaultMessage: 'Show field name',
                  id: 'FieldSettings.ShowFieldName',
                })}
                checked={getFieldSettings('showFieldName')}
                onChange={value => {
                  setFieldSettings('showFieldName', value)
                }}
              />
            )}
            <FormControl.Select
              name="displayStyle"
              label={$t({
                defaultMessage: 'Display style',
                id: 'PostsBlock.Settings.Fields.DisplayStyle',
              })}
              value={getFieldSettings('displayStyle') || displayStyles[2].value}
              onChange={value => {
                setFieldSettings(
                  'displayStyle',
                  value as ViewField['settings']['displayStyle'],
                )
              }}
              items={displayStyles}
            />
            {(field.settings?.displayStyle === 'truncated' ||
              field.settings?.displayStyle === 'expandable' ||
              field.settings?.displayStyle === undefined) && (
              <FormControl.Select
                name="truncateSize"
                label={$t({
                  defaultMessage: 'Content preview size',
                  id: 'PostsBlock.Settings.Fields.ContentPreviewSize',
                })}
                value={
                  getFieldSettings('truncateSize') || truncateSizes[1].value
                }
                onChange={value => {
                  setFieldSettings(
                    'truncateSize',
                    value as ViewField['settings']['truncateSize'],
                  )
                }}
                items={truncateSizes}
              />
            )}
          </>
        )}
        {!isSubField && field.id === 'replyList' && (
          <>
            {isSingleBlock && (
              <Toggle
                title="Show sorting options"
                checked={getFieldSettings('showReplySortOptions') ?? true}
                onChange={value => {
                  setFieldSettings('showReplySortOptions', !!value)
                }}
              />
            )}
            <FormControl.Select
              name="defaultReplySortId"
              label={$t({
                defaultMessage: 'Default sort',
                id: 'PostsBlock.Settings.Fields.DefaultSort',
              })}
              value={replySortId}
              onChange={value => {
                setFieldSettings(
                  'defaultReplySortId',
                  value as ViewField['settings']['defaultReplySortId'],
                )
              }}
              items={replySortOptions.map(option => ({
                value: option.id,
                text: option.name,
              }))}
            />
          </>
        )}
        {field?.fields && (
          <div>
            {field.fields
              .filter(item => item.locked)
              .map(item => (
                <NonDraggableItem
                  key={item.id}
                  label={item.label}
                  icon={item.icon}
                  checked={item.enabled}
                  handleChange={() => {
                    toggleItem(item.id)
                  }}
                  onClick={() => showFieldSettings(item.id)}
                  onMouseEnter={() => setActiveFieldId(item.id)}
                  onMouseLeave={() => setActiveFieldId('')}
                />
              ))}
            <FieldsSortableList
              items={(field?.fields ?? []).filter(item => !item.locked)}
              onReorder={newItems => setProperty('fields', newItems)}
              onClick={showFieldSettings}
              onActivate={id => {
                if (!selectedFieldId) {
                  setActiveFieldId(id)
                }
              }}
              onToggle={toggleItem}
            />
          </div>
        )}
      </GenericFieldSettings>
      {selectedFieldId && (
        <FieldSettings
          field={field.fields?.find(field => field?.id === selectedFieldId)}
          viewStyle={viewStyle}
          postTypeSlugs={postTypeSlugs}
          setProperty={setFieldProperty}
          goBack={hideFieldSettings}
          isSubField
          isSingleBlock
        />
      )}
    </>
  )
}
